'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { LinkOutlined } from '@ant-design/icons'
import { useRouter, usePathname } from 'next/navigation'
import {
  SessionInstrumentStrip,
} from '@/app/client/dashboard/flagship/SessionInstrumentStrip'
import {
  quoteAgeState,
  quoteFreshnessLabel,
} from '@/app/client/dashboard/flagship/deskStatus'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import type { PlanTier } from '@/lib/stripe/types'
import { FeedbackState } from '@/shared/components/feedback/FeedbackState'
import { useClientDeskSession } from '@/shared/providers/context/ClientDeskSessionContext'
import { useClientAuth } from '@/domains/auth'

function planLabelFromTier(tier: PlanTier | 'none'): string {
  switch (tier) {
    case 'starter':
      return 'Operator'
    case 'advanced':
      return 'Desk'
    case 'professional':
      return 'Pro'
    case 'enterprise':
      return 'Enterprise'
    case 'free':
    case 'none':
    default:
      return 'Free Desk'
  }
}

function formatClock(date: Date): string {
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  const ss = String(date.getSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

function formatAsOf(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function ClientDeskSessionChrome({
  greeting = 'Desk session',
  planLabel,
  onRefresh,
  refreshing,
}: {
  greeting?: string
  planLabel?: string
  onRefresh?: () => void
  refreshing?: boolean
}) {
  const session = useClientDeskSession()
  const { user } = useClientAuth()
  const { tier } = usePlanEntitlements()
  const pathname = usePathname()
  const [clock, setClock] = useState('--:--:--')

  useEffect(() => {
    const tick = () => setClock(formatClock(new Date()))
    tick()
    const timer = window.setInterval(tick, 1_000)
    return () => window.clearInterval(timer)
  }, [])

  const connections = useMemo(
    () =>
      session.connections.map((connection) => ({
        id: connection.connection_id,
        label:
          connection.display_name ||
          connection.broker_account_id ||
          connection.venue_id,
      })),
    [session.connections]
  )
  const onTradeDesk =
    pathname === '/trading' || pathname.startsWith('/trading/')
  const asOf = onTradeDesk
    ? session.quoteAsOf
    : session.quoteAsOf ||
      session.workspace?.provenance?.generated_at ||
      session.workspace?.as_of
  const marketStatus = asOf
    ? quoteFreshnessLabel(quoteAgeState(asOf, Date.now()))
    : null

  return (
    <SessionInstrumentStrip
      greeting={greeting}
      simulated={session.isSimulated}
      developer={user?.dev_agent === true}
      environment={
        session.selectedConnection?.environment ||
        session.workspace?.account?.environment ||
        null
      }
      planLabel={planLabel || planLabelFromTier(tier)}
      connections={connections}
      selectedConnectionId={session.selectedConnectionId}
      path={session.path}
      realtime={session.realtime}
      clock={clock}
      asOf={formatAsOf(asOf)}
      marketStatus={marketStatus}
      autoRefresh={session.autoRefresh}
      refreshing={
        refreshing ??
        (session.connectionsLoading || session.workspaceLoading)
      }
      density={session.density}
      onRefresh={onRefresh || (() => void session.refresh())}
      onConnection={session.selectConnection}
      onToggleAuto={session.setAutoRefresh}
      onDensity={session.setDensity}
    />
  )
}

export function DeskConnectionBoundary({
  children,
  requireWorkspace = true,
}: {
  children: React.ReactNode
  requireWorkspace?: boolean
}) {
  const router = useRouter()
  const session = useClientDeskSession()
  const manageAccounts = useMemo(
    () => [
      {
        label: 'Manage accounts',
        tone: 'primary' as const,
        icon: <LinkOutlined aria-hidden />,
        onClick: () => router.push('/broker-integration'),
      },
    ],
    [router]
  )

  if (session.connectionsLoading && session.connections.length === 0) {
    return (
      <FeedbackState
        variant="loading"
        title="Loading trading accounts"
        description="Checking for an active account and its workspace snapshot."
        actions={manageAccounts}
      />
    )
  }

  if (session.connectionsError && session.connections.length === 0) {
    return (
      <FeedbackState
        variant="error"
        title="Trading accounts are unavailable"
        description="The account list could not be loaded. No workstation data is being inferred."
        actions={manageAccounts}
      />
    )
  }

  if (session.isNoConnection) {
    return (
      <FeedbackState
        variant="empty"
        title="No active trading account"
        description="Add and validate an account before opening this connection-scoped workstation."
        icon={<LinkOutlined />}
        actions={manageAccounts}
      />
    )
  }

  if (
    requireWorkspace &&
    session.selectedConnectionId &&
    session.workspaceLoading &&
    !session.workspace
  ) {
    return (
      <FeedbackState
        variant="loading"
        title="Loading account workspace"
        description="Reading balances, positions, and orders from the selected account."
        actions={manageAccounts}
      />
    )
  }

  if (
    requireWorkspace &&
    session.selectedConnectionId &&
    session.workspaceError &&
    !session.workspace
  ) {
    return (
      <FeedbackState
        variant="error"
        title="Account workspace is unavailable"
        description="The selected provider did not return a usable workspace snapshot. No substitute data is shown."
        actions={manageAccounts}
      />
    )
  }

  return <>{children}</>
}
